import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  Kanban,
  Loader2,
  Percent,
  Shuffle,
  SlidersHorizontal,
  ThumbsDown,
  TrendingUp,
  UsersRound,
} from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { useEsAdmin } from "../datos/useEsAdmin";
import { enlaceValido, umbralesValidos } from "../dominio/ajustes";
import type { EtapaPrevision, UmbralesSemaforo } from "../dominio/tipos";
import { claveEtapa } from "../dominio/etapas";
import type { Sale } from "@/components/atomic-crm/types";
import { AgendaAjustes } from "./AgendaAjustes";
import { EditorLista } from "./EditorLista";
import { EtapasAjustes } from "./EtapasAjustes";
import { UsuariosAjustes } from "./UsuariosAjustes";

type Icono = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type Seccion = {
  id: string;
  Icono: Icono;
  grupo: "ventas" | "listas" | "sistema";
};

/** Secciones de la barra lateral de Ajustes (Robert, 02/11/2026), por grupos. */
const SECCIONES: Seccion[] = [
  { id: "general", Icono: SlidersHorizontal, grupo: "ventas" },
  { id: "reparto", Icono: Shuffle, grupo: "ventas" },
  { id: "consultorias", Icono: CalendarClock, grupo: "ventas" },
  { id: "comisiones", Icono: Percent, grupo: "ventas" },
  { id: "prevision", Icono: TrendingUp, grupo: "ventas" },
  { id: "sectores", Icono: Building2, grupo: "listas" },
  { id: "motivos", Icono: ThumbsDown, grupo: "listas" },
  { id: "usuarios", Icono: UsersRound, grupo: "sistema" },
  { id: "etapas", Icono: Kanban, grupo: "sistema" },
];
const GRUPOS = ["ventas", "listas", "sistema"] as const;

/**
 * Ajustes de admin, con barra lateral (Robert, 02/11/2026): Ventas (General, Reparto de leads,
 * Consultorías, Comisiones y Previsión), Listas (Sectores y Motivos de pérdida, con «Añadir»,
 * no un texto libre) y Sistema (Usuarios y Etapas, solo lectura). Lo de cada setter (rol, %,
 * tope, comisión) va en Equipo.
 */
export const AjustesPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const esAdmin = useEsAdmin();
  const { ajustes, cargando } = useAjustes();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [umbrales, setUmbrales] = useState<UmbralesSemaforo>(ajustes.semaforo);
  const [maxIntentos, setMaxIntentos] = useState(ajustes.maxIntentos);
  const [sectores, setSectores] = useState(ajustes.sectores);
  const [motivos, setMotivos] = useState(ajustes.motivosNoInteresa);
  // 47: motivos tras la consultoría y tras la propuesta, y probabilidades de la previsión.
  const [motivosConsultoria, setMotivosConsultoria] = useState(
    ajustes.motivosConsultoria,
  );
  const [motivosPropuesta, setMotivosPropuesta] = useState(
    ajustes.motivosPropuesta,
  );
  const [probabilidades, setProbabilidades] = useState(ajustes.probabilidades);
  const [comisionPct, setComisionPct] = useState(ajustes.comisionPct);
  const [comisionConsultorPct, setComisionConsultorPct] = useState(
    ajustes.comisionConsultorPct,
  );
  const [topePendientes, setTopePendientes] = useState(ajustes.topePendientes);
  const [diasRecuperacion, setDiasRecuperacion] = useState(
    ajustes.diasRecuperacion,
  );
  const [limiteSinAsignar, setLimiteSinAsignar] = useState(
    ajustes.limiteSinAsignar,
  );
  const [diasFrio, setDiasFrio] = useState(ajustes.diasFrio);
  const [pushMinutos, setPushMinutos] = useState(ajustes.pushMinutosAntes);
  // 35: dueño de los leads del formulario de la web.
  const [responsableWeb, setResponsableWeb] = useState(ajustes.responsableWeb);
  // 37: dueño cuando la consultoría la hace solo un consultor.
  const [responsableConsultoria, setResponsableConsultoria] = useState(
    ajustes.responsableConsultoria,
  );
  const { data: usuarios = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });
  const [topeConsultorias, setTopeConsultorias] = useState(
    ajustes.topeConsultoriasSemana,
  );
  const [reservasUrl, setReservasUrl] = useState(ajustes.reservasUrl ?? "");
  // 41: calendario de consultorías propio.
  const [agenda, setAgenda] = useState(ajustes.agenda);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (cargando) return;
    setUmbrales(ajustes.semaforo);
    setMaxIntentos(ajustes.maxIntentos);
    setSectores(ajustes.sectores);
    setMotivos(ajustes.motivosNoInteresa);
    setMotivosConsultoria(ajustes.motivosConsultoria);
    setMotivosPropuesta(ajustes.motivosPropuesta);
    setProbabilidades(ajustes.probabilidades);
    setComisionPct(ajustes.comisionPct);
    setComisionConsultorPct(ajustes.comisionConsultorPct);
    setTopePendientes(ajustes.topePendientes);
    setDiasRecuperacion(ajustes.diasRecuperacion);
    setTopeConsultorias(ajustes.topeConsultoriasSemana);
    setLimiteSinAsignar(ajustes.limiteSinAsignar);
    setDiasFrio(ajustes.diasFrio);
    setPushMinutos(ajustes.pushMinutosAntes);
    setResponsableWeb(ajustes.responsableWeb);
    setResponsableConsultoria(ajustes.responsableConsultoria);
    setReservasUrl(ajustes.reservasUrl ?? "");
    setAgenda(ajustes.agenda);
  }, [ajustes, cargando]);

  // Un solo guardado para todas las secciones: siempre se escribe el conjunto completo.
  const guardar = async () => {
    if (!umbralesValidos(umbrales)) {
      notify("nevora.ajustes.error_umbrales", { type: "warning" });
      return;
    }
    if (
      !(comisionPct >= 0 && comisionPct <= 100) ||
      !(comisionConsultorPct >= 0 && comisionConsultorPct <= 100)
    ) {
      notify("nevora.ajustes.error_comision", { type: "warning" });
      return;
    }
    if (Object.values(probabilidades).some((v) => !(v >= 0 && v <= 100))) {
      notify("nevora.ajustes.error_probabilidad", { type: "warning" });
      return;
    }
    if (reservasUrl.trim() && !enlaceValido(reservasUrl)) {
      notify("nevora.ajustes.error_reservas", { type: "warning" });
      return;
    }
    setGuardando(true);
    try {
      await dataProvider.updateAjustes({
        semaforo: umbrales,
        maxIntentos: Math.max(1, Math.floor(maxIntentos)),
        sectores,
        motivosNoInteresa: motivos,
        motivosConsultoria,
        motivosPropuesta,
        probabilidades,
        comisionPct,
        comisionConsultorPct,
        topePendientes: Math.max(0, Math.floor(topePendientes)),
        diasRecuperacion: Math.max(1, Math.floor(diasRecuperacion)),
        topeConsultoriasSemana: Math.max(1, Math.floor(topeConsultorias)),
        limiteSinAsignar: Math.max(0, Math.floor(limiteSinAsignar)),
        diasFrio: Math.max(1, Math.floor(diasFrio)),
        pushMinutosAntes: Math.min(240, Math.max(1, Math.floor(pushMinutos))),
        responsableWeb,
        responsableConsultoria,
        reservasUrl: enlaceValido(reservasUrl),
        // 41: al encenderlo se apunta desde cuándo (las citas de antes ya están en info@).
        agenda: {
          ...agenda,
          propiaDesde: agenda.propia
            ? ajustes.agenda.propia && ajustes.agenda.propiaDesde
              ? ajustes.agenda.propiaDesde
              : new Date().toISOString()
            : null,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["configuration"] });
      notify("nevora.ajustes.guardado", { type: "success" });
    } catch {
      notify("nevora.ajustes.error_guardar", { type: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const botonGuardar = (
    <Button onClick={guardar} disabled={guardando || cargando}>
      {guardando ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
      {translate("nevora.ajustes.guardar")}
    </Button>
  );

  const campoUmbral = (clave: keyof UmbralesSemaforo) => (
    <div className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0">
      <Label htmlFor={`umbral-${clave}`}>
        {translate(`nevora.ajustes.${clave}`)}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={`umbral-${clave}`}
          type="number"
          min={0}
          className="w-24 text-right"
          value={umbrales[clave]}
          onChange={(e) =>
            setUmbrales({ ...umbrales, [clave]: Number(e.target.value) })
          }
        />
        <span className="text-sm text-muted-foreground w-8">
          {translate("nevora.ajustes.dias")}
        </span>
      </div>
    </div>
  );

  const campoNumero = (
    id: string,
    etiqueta: string,
    valor: number,
    cambiar: (n: number) => void,
    unidad?: string,
    paso = 1,
  ) => (
    <div className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0">
      <Label htmlFor={id}>{translate(etiqueta)}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={0}
          step={paso}
          className="w-24 text-right"
          value={valor}
          onChange={(e) => cambiar(Number(e.target.value))}
        />
        <span className="text-sm text-muted-foreground w-10">
          {unidad ? translate(unidad) : ""}
        </span>
      </div>
    </div>
  );

  const tarjeta = "superficie px-5 py-4";
  const cabecera = (id: string) => (
    <header className="space-y-1">
      <h2 className="titulo-seccion text-lg">
        {translate(`nevora.ajustes.nav.${id}`)}
      </h2>
      <p className="text-sm text-muted-foreground">
        {translate(`nevora.ajustes.descripciones.${id}`)}
      </p>
    </header>
  );
  const selectorUsuario = (
    id: string,
    valor: number | null,
    cambiar: (v: number | null) => void,
    vacio: string,
    soloAdmins = false,
  ) => (
    <select
      id={id}
      className="h-9 rounded-md border bg-background px-2 text-sm"
      value={valor ?? ""}
      onChange={(e) =>
        cambiar(e.target.value === "" ? null : Number(e.target.value))
      }
    >
      <option value="">{translate(vacio)}</option>
      {usuarios
        .filter(
          (u) =>
            (!soloAdmins || u.administrator) && (!u.disabled || u.id === valor),
        )
        .map((u) => (
          <option key={String(u.id)} value={String(u.id)}>
            {`${u.first_name} ${u.last_name}`}
          </option>
        ))}
    </select>
  );

  const cuerpo = !esAdmin ? (
    <p className="text-muted-foreground">
      {translate("nevora.ajustes.solo_admin")}
    </p>
  ) : (
    <Tabs
      defaultValue="general"
      orientation="vertical"
      className="gap-6 pb-8 md:flex-row md:items-start"
    >
      <nav
        className="md:sticky md:top-4 md:w-56 md:shrink-0"
        aria-label={translate("nevora.ajustes.titulo")}
      >
        <TabsList className="h-auto w-full flex-row items-stretch justify-start gap-0.5 overflow-x-auto bg-transparent p-0 md:flex-col md:overflow-visible">
          {GRUPOS.map((g) => (
            <div key={g} className="contents md:block md:space-y-0.5 md:pb-4">
              <p className="etiqueta hidden px-2.5 pb-1.5 md:block">
                {translate(`nevora.ajustes.grupos.${g}`)}
              </p>
              {SECCIONES.filter((x) => x.grupo === g).map(({ id, Icono }) => (
                <TabsTrigger
                  key={id}
                  value={id}
                  className={cn(
                    "h-auto flex-none justify-start gap-2.5 rounded-lg px-2.5 py-2 text-muted-foreground md:w-full",
                    "hover:bg-foreground/[0.04] hover:text-foreground",
                    "data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-tarjeta data-[state=active]:ring-1 data-[state=active]:ring-border/60",
                  )}
                >
                  <Icono className="size-4" aria-hidden />
                  {translate(`nevora.ajustes.nav.${id}`)}
                </TabsTrigger>
              ))}
            </div>
          ))}
        </TabsList>
      </nav>

      <div className="min-w-0 max-w-2xl flex-1">
        <TabsContent value="general" className="space-y-5">
          {cabecera("general")}
          <section className={tarjeta}>
            <h3 className="etiqueta mb-1">
              {translate("nevora.ajustes.semaforo")}
            </h3>
            <p className="text-xs text-muted-foreground mb-2">
              {translate("nevora.ajustes.semaforo_ayuda")}
            </p>
            {campoUmbral("verde")}
            {campoUmbral("azul")}
            {campoUmbral("amarillo")}
            {campoUmbral("naranja")}
          </section>
          <section className={tarjeta}>
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="max-intentos">
                {translate("nevora.ajustes.max_intentos")}
              </Label>
              <Input
                id="max-intentos"
                type="number"
                min={1}
                className="w-24 text-right"
                value={maxIntentos}
                onChange={(e) => setMaxIntentos(Number(e.target.value))}
              />
            </div>
          </section>
          {botonGuardar}
        </TabsContent>

        <TabsContent value="reparto" className="space-y-5">
          {cabecera("reparto")}
          <section className={tarjeta}>
            <p className="text-xs text-muted-foreground mb-2">
              {translate("nevora.ajustes.reparto_ayuda")}
            </p>
            {campoNumero(
              "tope-pendientes",
              "nevora.ajustes.tope_pendientes",
              topePendientes,
              setTopePendientes,
              "nevora.ajustes.leads",
            )}
            {campoNumero(
              "dias-recuperacion",
              "nevora.ajustes.dias_recuperacion",
              diasRecuperacion,
              setDiasRecuperacion,
              "nevora.ajustes.dias",
            )}
          </section>
          <section className={tarjeta}>
            <h3 className="etiqueta">{translate("nevora.ajustes.prospect")}</h3>
            <p className="text-xs text-muted-foreground mb-2">
              {translate("nevora.ajustes.prospect_ayuda")}
            </p>
            {campoNumero(
              "limite-sin-asignar",
              "nevora.ajustes.limite_sin_asignar",
              limiteSinAsignar,
              setLimiteSinAsignar,
              "nevora.ajustes.leads",
            )}
            {campoNumero(
              "dias-frio",
              "nevora.ajustes.dias_frio",
              diasFrio,
              setDiasFrio,
              "nevora.ajustes.dias",
            )}
          </section>
          <section className={cn(tarjeta, "space-y-2")}>
            <h3 className="etiqueta">{translate("nevora.ajustes.web")}</h3>
            <p className="text-xs text-muted-foreground">
              {translate("nevora.ajustes.web_ayuda")}
            </p>
            <div className="flex items-center justify-between gap-4 py-2">
              <Label htmlFor="responsable-web">
                {translate("nevora.ajustes.responsable_web")}
              </Label>
              {selectorUsuario(
                "responsable-web",
                responsableWeb,
                setResponsableWeb,
                "nevora.ajustes.sin_responsable",
              )}
            </div>
          </section>
          {botonGuardar}
        </TabsContent>

        <TabsContent value="consultorias" className="space-y-5">
          {cabecera("consultorias")}
          <section className={cn(tarjeta, "space-y-2")}>
            <h3 className="etiqueta">{translate("nevora.ajustes.agenda")}</h3>
            {campoNumero(
              "tope-consultorias",
              "nevora.ajustes.tope_consultorias",
              topeConsultorias,
              setTopeConsultorias,
            )}
            {campoNumero(
              "push-minutos",
              "nevora.ajustes.push_minutos",
              pushMinutos,
              setPushMinutos,
              "nevora.ajustes.minutos",
            )}
            {agenda.propia ? (
              // 41: con el calendario propio, el enlace de reservas no se usa (nunca los dos a la vez).
              <p className="text-xs text-muted-foreground pt-1">
                {translate("nevora.agenda_propia.reservas_sustituido")}
              </p>
            ) : (
              <div className="space-y-1 pt-1">
                <Label htmlFor="reservas-url">
                  {translate("nevora.ajustes.reservas_url")}
                </Label>
                <Input
                  id="reservas-url"
                  type="url"
                  placeholder="https://calendar.app.google/…"
                  value={reservasUrl}
                  onChange={(e) => setReservasUrl(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {translate("nevora.ajustes.reservas_ayuda")}
                </p>
              </div>
            )}
          </section>
          <AgendaAjustes valor={agenda} cambiar={setAgenda} />
          <section className={cn(tarjeta, "space-y-2")}>
            <h3 className="etiqueta">
              {translate("nevora.ajustes.consultoria")}
            </h3>
            <p className="text-xs text-muted-foreground">
              {translate("nevora.ajustes.consultoria_ayuda")}
            </p>
            <div className="flex items-center justify-between gap-4 py-2">
              <Label htmlFor="responsable-consultoria">
                {translate("nevora.ajustes.responsable_consultoria")}
              </Label>
              {selectorUsuario(
                "responsable-consultoria",
                responsableConsultoria,
                setResponsableConsultoria,
                "nevora.ajustes.responsable_consultoria_defecto",
                true,
              )}
            </div>
          </section>
          {botonGuardar}
        </TabsContent>

        <TabsContent value="comisiones" className="space-y-5">
          {cabecera("comisiones")}
          <section className={tarjeta}>
            <p className="text-xs text-muted-foreground mb-2">
              {translate("nevora.ajustes.comisiones_ayuda")}
            </p>
            {campoNumero(
              "comision-pct",
              "nevora.ajustes.comision_global",
              comisionPct,
              setComisionPct,
              "nevora.ajustes.por_ciento",
              0.5,
            )}
            {campoNumero(
              "comision-consultor-pct",
              "nevora.ajustes.comision_consultor",
              comisionConsultorPct,
              setComisionConsultorPct,
              "nevora.ajustes.por_ciento",
              0.5,
            )}
          </section>
          {botonGuardar}
        </TabsContent>

        <TabsContent value="prevision" className="space-y-5">
          {cabecera("prevision")}
          <section className={tarjeta}>
            <p className="text-xs text-muted-foreground mb-2">
              {translate("nevora.ajustes.prevision_ayuda")}
            </p>
            {(Object.keys(probabilidades) as EtapaPrevision[]).map((etapa) =>
              campoNumero(
                `probabilidad-${etapa}`,
                claveEtapa(etapa),
                probabilidades[etapa],
                (n) => setProbabilidades({ ...probabilidades, [etapa]: n }),
                "nevora.ajustes.por_ciento",
                5,
              ),
            )}
          </section>
          {botonGuardar}
        </TabsContent>

        <TabsContent value="sectores" className="space-y-5">
          {cabecera("sectores")}
          <EditorLista
            id="sectores"
            titulo={translate("nevora.ajustes.sectores_lista")}
            ayuda={translate("nevora.ajustes.sectores_ayuda")}
            valores={sectores}
            cambiar={setSectores}
            textoAnadir={translate("nevora.ajustes.lista.anadir_sector")}
            placeholder={translate("nevora.ajustes.lista.nuevo_sector")}
          />
          {botonGuardar}
        </TabsContent>

        <TabsContent value="motivos" className="space-y-5">
          {cabecera("motivos")}
          {(
            [
              ["motivos-llamada", "motivos_lista", motivos, setMotivos],
              [
                "motivos-consultoria",
                "motivos_consultoria",
                motivosConsultoria,
                setMotivosConsultoria,
              ],
              [
                "motivos-propuesta",
                "motivos_propuesta",
                motivosPropuesta,
                setMotivosPropuesta,
              ],
            ] as const
          ).map(([id, etiqueta, valores, cambiar]) => (
            <EditorLista
              key={id}
              id={id}
              titulo={translate(`nevora.ajustes.${etiqueta}`)}
              valores={valores}
              cambiar={cambiar}
              textoAnadir={translate("nevora.ajustes.lista.anadir_motivo")}
              placeholder={translate("nevora.ajustes.lista.nuevo_motivo")}
            />
          ))}
          {botonGuardar}
        </TabsContent>

        <TabsContent value="usuarios" className="space-y-5">
          {cabecera("usuarios")}
          <UsuariosAjustes />
        </TabsContent>

        <TabsContent value="etapas" className="space-y-5">
          {cabecera("etapas")}
          <EtapasAjustes />
        </TabsContent>
      </div>
    </Tabs>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">
            {translate("nevora.ajustes.titulo")}
          </h1>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return (
    <div className="mt-2 space-y-4">
      <h1 className="titulo-pagina">{translate("nevora.ajustes.titulo")}</h1>
      {cuerpo}
    </div>
  );
};

AjustesPage.path = "/ajustes";
